import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Form, Button, Card, Alert, Spinner,
  Modal, Table,
  InputGroup
} from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import { specialBookingsAPI, driversCompanyAPI } from '../../api/apiClient';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { FaPlus, FaSearch, FaTrash, FaTruck } from 'react-icons/fa';
import LocationAutocomplete from '../LocationAutocomplete';
import { useSearchFilter } from '../useSearchFilter';

const SHIPMENT_TYPES = {
  Palette: { weight: 100, length: 120, width: 80, height: 100 },
  Paket: { weight: 10, length: 40, width: 40, height: 40 },
  Dokument: { weight: '', length: '', width: '', height: '' },
  Fhrrader: { weight: '', length: '', width: '', height: '' },
  'Komplettes Fahrzeug': { weight: '', length: '', width: '', height: '' },
  Sonstige: { weight: '', length: '', width: '', height: '' }
};

const SpecialBookingCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [showDriversModal, setShowDriversModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const filteredData = useSearchFilter(drivers, searchTerm, ['email']);

  const [formData, setFormData] = useState({
    customer_id: '',
    booking_from: [''],
    booking_to: [''],
    shipment_data: [{
      quantity: 1,
      shipment_data: 'Palette',
      weight: '',
      height: '',
      length: '',
      width: ''
    }],
    loading_company: [''],
    loading_first_name: [''],
    loading_last_name: [''],
    loading_streat_no: [''],
    loading_address_additional: [''],
    loading_phone: [''],
    loading_reffernce: [''],
    loading_date_time: [''],
    unloading_company: [''],
    unloading_first_name: [''],
    unloading_last_name: [''],
    unloading_streat_no: [''],
    unloading_address_additional: [''],
    unloading_phone: [''],
    unloading_reffernce: [''],
    unloading_date_time: [''],
    extra_service: [],
    message: '',
    order_driver: ''
  });

  const [dateTimeData, setDateTimeData] = useState({
    loading_dates: [''],
    loading_start_times: [''],
    loading_end_times: [''],
    unloading_dates: [''],
    unloading_start_times: [''],
    unloading_end_times: ['']
  });

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const response = await driversCompanyAPI.index();
      if (response.success) {
        setDrivers(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching drivers:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateDimensions = (index, shipmentType) => {
    const dimensions = SHIPMENT_TYPES[shipmentType] || {};
    
    setFormData(prev => {
      const newShipmentData = [...prev.shipment_data];
      newShipmentData[index] = {
        ...newShipmentData[index],
        shipment_data: shipmentType,
        weight: dimensions.weight || '',
        length: dimensions.length || '',
        width: dimensions.width || '',
        height: dimensions.height || ''
      };
      
      return { ...prev, shipment_data: newShipmentData };
    });
  };

  const handleInputChange = (section, index, field, value) => {
    setFormData(prev => {
      const newSection = [...prev[section]];
      if (section === 'shipment_data') {
        if (field === 'shipment_data') {
          updateDimensions(index, value);
          return prev; 
        }
        
        newSection[index] = { ...newSection[index], [field]: value };
      } else {
        newSection[index] = value;
      }
      return { ...prev, [section]: newSection };
    });
  };

  const handleDateTimeChange = (type, index, value) => {
    setDateTimeData(prev => ({
      ...prev,
      [type]: prev[type].map((item, i) => i === index ? value : item)
    }));
  };

  const formatDateTime = (date, startTime, endTime) => {
    if (!date) return '';
    const formattedDate = date; 
    return `${formattedDate} ${startTime || '00:00'} - ${endTime || '00:00'}`;
  };

  const addLocation = (type) => {
    setFormData(prev => ({
      ...prev,
      [type]: [...prev[type], '']
    }));
    
    setDateTimeData(prev => ({
      ...prev,
      [`${type.includes('loading') ? 'loading' : 'unloading'}_dates`]: [...prev[`${type.includes('loading') ? 'loading' : 'unloading'}_dates`], ''],
      [`${type.includes('loading') ? 'loading' : 'unloading'}_start_times`]: [...prev[`${type.includes('loading') ? 'loading' : 'unloading'}_start_times`], ''],
      [`${type.includes('loading') ? 'loading' : 'unloading'}_end_times`]: [...prev[`${type.includes('loading') ? 'loading' : 'unloading'}_end_times`], '']
    }));
  };

  const removeLocation = (type, index) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
    
    const prefix = type.includes('loading') ? 'loading' : 'unloading';
    setDateTimeData(prev => ({
      ...prev,
      [`${prefix}_dates`]: prev[`${prefix}_dates`].filter((_, i) => i !== index),
      [`${prefix}_start_times`]: prev[`${prefix}_start_times`].filter((_, i) => i !== index),
      [`${prefix}_end_times`]: prev[`${prefix}_end_times`].filter((_, i) => i !== index)
    }));
  };

  const addShipment = () => {
    setFormData(prev => ({
      ...prev,
      shipment_data: [...prev.shipment_data, {
        quantity: 1,
        shipment_data: 'Palette',
        weight: SHIPMENT_TYPES.Palette.weight,
        height: SHIPMENT_TYPES.Palette.height,
        length: SHIPMENT_TYPES.Palette.length,
        width: SHIPMENT_TYPES.Palette.width
      }]
    }));
  };

  const removeShipment = (index) => {
    setFormData(prev => ({
      ...prev,
      shipment_data: prev.shipment_data.filter((_, i) => i !== index)
    }));
  };

  const handleExtraService = (service) => {
    setFormData(prev => ({
      ...prev,
      extra_service: prev.extra_service.includes(service)
        ? prev.extra_service.filter(s => s !== service)
        : [...prev.extra_service, service]
    }));
  };

  const handleLocationChange = (e, index) => {
    if (e.target.name.endsWith('_from') || e.target.name.endsWith('_to')) {
      setFormData(prev => {
        const newArray = [...prev[e.target.name]];
        newArray[index] = e.target.value;
        return { 
          ...prev, 
          [e.target.name]: newArray,
          ...(e.lat && { [`${e.target.name}_lat`]: e.lat }),
          ...(e.lng && { [`${e.target.name}_lng`]: e.lng })
        };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [e.target.name]: e.target.value,
        ...(e.lat && { [`${e.target.name}_lat`]: e.lat }),
        ...(e.lng && { [`${e.target.name}_lng`]: e.lng })
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setErrors({});

    try {
      const dataToSend = new FormData();
      
      // إضافة الحقول الأساسية
      dataToSend.append('customer_id', formData.customer_id);
      dataToSend.append('message', formData.message);
      if (formData.customer_id) {
        dataToSend.append('customer_id', formData.customer_id);
      }

      formData.booking_from.forEach((value, index) => {
        dataToSend.append(`booking_from[${index}]`, value);
      });

      formData.booking_to.forEach((value, index) => {
        dataToSend.append(`booking_to[${index}]`, value);
      });

      formData.loading_company.forEach((value, index) => {
        dataToSend.append(`loading_company[${index}]`, value);
        dataToSend.append(`loading_first_name[${index}]`, formData.loading_first_name[index] || '');
        dataToSend.append(`loading_last_name[${index}]`, formData.loading_last_name[index] || '');
        dataToSend.append(`loading_streat_no[${index}]`, formData.loading_streat_no[index] || '');
        dataToSend.append(`loading_address_additional[${index}]`, formData.loading_address_additional[index] || '');
        dataToSend.append(`loading_reffernce[${index}]`, formData.loading_reffernce[index] || '');
        dataToSend.append(`loading_phone[${index}]`, formData.loading_phone[index] || '');
        
        const loadingDateTime = formatDateTime(
          dateTimeData.loading_dates[index],
          dateTimeData.loading_start_times[index],
          dateTimeData.loading_end_times[index]
        );
        dataToSend.append(`loading_date_time[${index}]`, loadingDateTime);
      });

      formData.unloading_company.forEach((value, index) => {
        dataToSend.append(`unloading_company[${index}]`, value);
        dataToSend.append(`unloading_first_name[${index}]`, formData.unloading_first_name[index] || '');
        dataToSend.append(`unloading_last_name[${index}]`, formData.unloading_last_name[index] || '');
        dataToSend.append(`unloading_streat_no[${index}]`, formData.unloading_streat_no[index] || '');
        dataToSend.append(`unloading_address_additional[${index}]`, formData.unloading_address_additional[index] || '');
        dataToSend.append(`unloading_reffernce[${index}]`, formData.unloading_reffernce[index] || '');
        dataToSend.append(`unloading_phone[${index}]`, formData.unloading_phone[index] || '');
        
        const unloadingDateTime = formatDateTime(
          dateTimeData.unloading_dates[index],
          dateTimeData.unloading_start_times[index],
          dateTimeData.unloading_end_times[index]
        );
        dataToSend.append(`unloading_date_time[${index}]`, unloadingDateTime);
      });

      formData.shipment_data.forEach((item, index) => {
        dataToSend.append(`shipment_data[${index}][quantity]`, item.quantity);
        dataToSend.append(`shipment_data[${index}][shipment_data]`, item.shipment_data);
        dataToSend.append(`shipment_data[${index}][weight]`, item.weight);
        dataToSend.append(`shipment_data[${index}][height]`, item.height);
        dataToSend.append(`shipment_data[${index}][length]`, item.length);
        dataToSend.append(`shipment_data[${index}][width]`, item.width);
      });

      formData.extra_service.forEach((service, index) => {
        dataToSend.append(`extra_service[${index}]`, service);
      });

      console.log('Sending data:');
      for (let [key, value] of dataToSend.entries()) {
        console.log(key, value);
      }

      const response = await specialBookingsAPI.store(dataToSend);
      
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('booking_created_successfully'),
          timer: 3000,
          showConfirmButton: false
        });
        navigate('/admin/special-bookings');
      } else {
        if (response.errors) {
            setErrors(t(response.errors));
        }
        setError(t(response.message) || t('failed_to_create_booking'));
      }
    } catch (err) {
      setError(t('failed_to_create_booking'));
      console.error('Error creating booking:', err);
    } finally {
      setSaving(false);
    }
  };

  const selectDriver = (driver) => {
    setFormData(prev => ({ ...prev, customer_id: driver.id }));
    setShowDriversModal(false);
  };

  const renderErrors = () => {
    if (Object.keys(errors).length === 0) return null;

    return (
      <Alert variant="danger">
        <h6>{t('validation_errors')}</h6>
        <ul className="mb-0">
          {Object.entries(errors).map(([field, fieldErrors]) => (
            <li key={field}>
              <strong>{field}:</strong> {Array.isArray(fieldErrors) ? fieldErrors.join(', ') : fieldErrors}
            </li>
          ))}
        </ul>
      </Alert>
    );
  };

  // مكون لرسم حقل الشحنة
  const ShipmentField = ({ item, index, onRemove }) => (
    <div className="border p-3 mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6>{t('shipment')} {index + 1}</h6>
        {formData.shipment_data.length > 1 && (
          <Button variant="outline-danger" size="sm" onClick={() => onRemove(index)}>
            <FaTrash />
          </Button>
        )}
      </div>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('booking.quantity')}</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={item.quantity}
              onChange={(e) => handleInputChange('shipment_data', index, 'quantity', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('booking.type')}</Form.Label>
            <Form.Select
              value={item.shipment_data}
              onChange={(e) => handleInputChange('shipment_data', index, 'shipment_data', e.target.value)}
            >
              {Object.keys(SHIPMENT_TYPES).map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('booking.weight')} (kg)</Form.Label>
            <Form.Control
              min={0}
              type="number"
              value={item.weight}
              onChange={(e) => handleInputChange('shipment_data', index, 'weight', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('booking.length')} (cm)</Form.Label>
            <Form.Control
              min={0}
              type="number"
              value={item.length}
              onChange={(e) => handleInputChange('shipment_data', index, 'length', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('booking.width')} (cm)</Form.Label>
            <Form.Control
              min={0}
              type="number"
              value={item.width}
              onChange={(e) => handleInputChange('shipment_data', index, 'width', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('booking.height')} (cm)</Form.Label>
            <Form.Control
              min={0}
              type="number"
              value={item.height}
              onChange={(e) => handleInputChange('shipment_data', index, 'height', e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );

  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <Container fluid className="p-3 bg-light min-vh-100">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{t('create_special_booking')}</h2>
        <Button variant="secondary" onClick={() => navigate('/admin/special-bookings')}>
          {t('back_to_list')}
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {renderErrors()}

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={8}>
            {/* أماكن التحميل */}
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{t('loading_locations')}</h5>
                <Button size="sm" onClick={() => addLocation('loading_company')}>
                  <FaPlus /> {t('add_location')}
                </Button>
              </Card.Header>
              <Card.Body>
                {formData.loading_company.map((_, index) => (
                  <div key={index} className="border p-3 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6>{t('loading_location')} {index + 1}</h6>
                      {formData.loading_company.length > 1 && (
                        <Button variant="outline-danger" size="sm" 
                          onClick={() => removeLocation('loading_company', index)}>
                          <FaTrash />
                        </Button>
                      )}
                    </div>
                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.loading_company')}</Form.Label>
                            <Form.Control
                                value={formData.loading_company[index]}
                                onChange={(e) => handleInputChange('loading_company', index, '', e.target.value)}
                                required
                            />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.loading_first_name')}</Form.Label>
                            <Form.Control
                                value={formData.loading_first_name[index]}
                                onChange={(e) => handleInputChange('loading_first_name', index, '', e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.loading_last_name')}</Form.Label>
                            <Form.Control
                                value={formData.loading_last_name[index]}
                                onChange={(e) => handleInputChange('loading_last_name', index, '', e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.loading_streat_no')}</Form.Label>
                            <Form.Control
                                value={formData.loading_streat_no[index]}
                                onChange={(e) => handleInputChange('loading_streat_no', index, '', e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.loading_address_additional')}</Form.Label>
                            <Form.Control
                                value={formData.loading_address_additional[index]}
                                onChange={(e) => handleInputChange('loading_address_additional', index, '', e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group className="mb-3" controlId="formPostalCode">
                                <Form.Label>{t('booking.booking_from')}</Form.Label>
                               <LocationAutocomplete
                                name="booking_from"
                                value={formData.booking_from[index]}
                                onChange={(e) => handleLocationChange(e, index)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.loading_phone')}</Form.Label>
                            <Form.Control
                                value={formData.loading_phone[index]}
                                onChange={(e) => handleInputChange('loading_phone', index, '', e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.loading_reffernce')}</Form.Label>
                            <Form.Control
                                value={formData.loading_reffernce[index]}
                                onChange={(e) => handleInputChange('loading_reffernce', index, '', e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.date')} *</Form.Label>
                            <Form.Control
                                type="date"
                                value={dateTimeData.loading_dates[index]}
                                onChange={(e) => handleDateTimeChange('loading_dates', index, e.target.value)}
                                required
                            />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.start_time')}</Form.Label>
                            <Form.Control
                                type="time"
                                value={dateTimeData.loading_start_times[index]}
                                onChange={(e) => handleDateTimeChange('loading_start_times', index, e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.end_time')}</Form.Label>
                            <Form.Control
                                type="time"
                                value={dateTimeData.loading_end_times[index]}
                                onChange={(e) => handleDateTimeChange('loading_end_times', index, e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group>
                            <Form.Control
                                plaintext
                                readOnly
                                value={formatDateTime(
                                dateTimeData.loading_dates[index],
                                dateTimeData.loading_start_times[index],
                                dateTimeData.loading_end_times[index]
                                )}
                                className="text-muted small"
                            />
                            </Form.Group>
                        </Col>
                    </Row>
                  </div>
                ))}
              </Card.Body>
            </Card>

            {/* أماكن التنزيل */}
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{t('unloading_locations')}</h5>
                <Button size="sm" onClick={() => addLocation('unloading_company')}>
                  <FaPlus /> {t('add_location')}
                </Button>
              </Card.Header>
              <Card.Body>
                {formData.unloading_company.map((_, index) => (
                  <div key={index} className="border p-3 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6>{t('booking.unloading_location')} {index + 1}</h6>
                      {formData.unloading_company.length > 1 && (
                        <Button variant="outline-danger" size="sm" 
                          onClick={() => removeLocation('unloading_company', index)}>
                          <FaTrash />
                        </Button>
                      )}
                    </div>
                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.unloading_company')} *</Form.Label>
                            <Form.Control
                                value={formData.unloading_company[index]}
                                onChange={(e) => handleInputChange('unloading_company', index, '', e.target.value)}
                                required
                            />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.unloading_first_name')}</Form.Label>
                            <Form.Control
                                value={formData.unloading_first_name[index]}
                                onChange={(e) => handleInputChange('unloading_first_name', index, '', e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.unloading_last_name')}</Form.Label>
                            <Form.Control
                                value={formData.unloading_last_name[index]}
                                onChange={(e) => handleInputChange('unloading_last_name', index, '', e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.unloading_streat_no')}</Form.Label>
                            <Form.Control
                                value={formData.unloading_streat_no[index]}
                                onChange={(e) => handleInputChange('unloading_streat_no', index, '', e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.unloading_address_additional')}</Form.Label>
                            <Form.Control
                                value={formData.unloading_address_additional[index]}
                                onChange={(e) => handleInputChange('unloading_address_additional', index, '', e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group className="mb-3" controlId="formPostalCode">
                                <Form.Label>{t('booking.booking_to')}</Form.Label>
                                <LocationAutocomplete
                                name="booking_to"
                                value={formData.booking_to[index]}
                                onChange={(e) => handleLocationChange(e, index)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.unloading_phone')}</Form.Label>
                            <Form.Control
                                value={formData.unloading_phone[index]}
                                onChange={(e) => handleInputChange('unloading_phone', index, '', e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.unloading_reffernce')}</Form.Label>
                            <Form.Control
                                value={formData.unloading_reffernce[index]}
                                onChange={(e) => handleInputChange('unloading_reffernce', index, '', e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.date')} *</Form.Label>
                            <Form.Control
                                type="date"
                                value={dateTimeData.unloading_dates[index]}
                                onChange={(e) => handleDateTimeChange('unloading_dates', index, e.target.value)}
                                required
                            />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.start_time')}</Form.Label>
                            <Form.Control
                                type="time"
                                value={dateTimeData.unloading_start_times[index]}
                                onChange={(e) => handleDateTimeChange('unloading_start_times', index, e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                            <Form.Label>{t('booking.end_time')}</Form.Label>
                            <Form.Control
                                type="time"
                                value={dateTimeData.unloading_end_times[index]}
                                onChange={(e) => handleDateTimeChange('unloading_end_times', index, e.target.value)}
                            />
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group>
                            <Form.Control
                                plaintext
                                readOnly
                                value={formatDateTime(
                                dateTimeData.unloading_dates[index],
                                dateTimeData.unloading_start_times[index],
                                dateTimeData.unloading_end_times[index]
                                )}
                                className="text-muted small"
                            />
                            </Form.Group>
                        </Col>
                    </Row>
                  </div>
                ))}
              </Card.Body>
            </Card>

            {/* بيانات الشحنة */}
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{t('shipment_data')}</h5>
                <Button size="sm" onClick={addShipment}>
                  <FaPlus /> {t('booking.add_shipment')}
                </Button>
              </Card.Header>
              <Card.Body>
                {formData.shipment_data.map((item, index) => (
                  <ShipmentField 
                    key={index} 
                    item={item} 
                    index={index} 
                    onRemove={removeShipment}
                  />
                ))}
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            {/* السائق */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">{t('driver_assignment')}</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>{t('select_driver')}</Form.Label>
                  <div className="d-grid">
                    <Button variant="outline-primary" onClick={() => setShowDriversModal(true)}>
                      <FaTruck /> {t('select_driver')}
                    </Button>
                  </div>
                  {formData.customer_id && (
                    <div className="mt-2 p-2 bg-light rounded">
                      <strong>{t('selected_driver')}: </strong>
                      {drivers.find(d => d.id === formData.customer_id)?.firstname} {drivers.find(d => d.id === formData.customer_id)?.lastname}
                    </div>
                  )}
                </Form.Group>
              </Card.Body>
            </Card>

            {/* خدمات إضافية */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">{t('extra_services')}</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group>
                  {['loading', 'unloading'].map(service => (
                    <Form.Check
                      key={service}
                      type="checkbox"
                      label={t(service)}
                      checked={formData.extra_service.includes(service)}
                      onChange={() => handleExtraService(service)}
                      className="mb-2"
                    />
                  ))}
                </Form.Group>
              </Card.Body>
            </Card>

            {/* ملاحظات */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">{t('notes')}</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    placeholder={t('notes_placeholder')}
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            {/* زر الحفظ */}
            <div className="d-grid">
              <Button type="submit" className='btn btn-color' size="lg" disabled={saving}>
                {saving ? <Spinner size="sm" className="me-2" /> : null}
                {saving ? t('saving') : t('booking.create_booking')}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>

      {/* Modal لاختيار السائق */}
      <Modal show={showDriversModal} onHide={() => setShowDriversModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('select_driver')}</Modal.Title>
        </Modal.Header>

        {/* Search Bar */}
        <Row className="m-3">
          <Col md={6}>
            <InputGroup>
              <InputGroup.Text><FaSearch /></InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t('search_by_email')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button 
                  variant="outline-secondary" 
                  onClick={clearSearch}
                  title={t("reset_search")}
                >
                  ✕
                </Button>
              )}
            </InputGroup>
          </Col>
        </Row>

        <Modal.Body>
          {loading ? (
            <div className="text-center">
              <Spinner />
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>{t('name')}</th>
                    <th>{t('email')}</th>
                    <th>{t('company')}</th>
                    <th>{t('vehicle_type')}</th>
                    <th>{t('action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map(driver => (
                    <tr key={driver.id}>
                      <td>{driver.firstname} {driver.lastname}</td>
                      <td>
                        {searchTerm ? (
                          <span 
                            dangerouslySetInnerHTML={{
                              __html: driver.email?.replace(
                                new RegExp(searchTerm, 'gi'), 
                                match => `<mark class="bg-warning">${match}</mark>`
                              )
                            }} 
                          />
                        ) : (
                          driver.email
                        )}
                      </td>
                      <td>{driver.company_name}</td>
                      <td>{driver.vehicle_type}</td>
                      <td>
                        <Button
                          size="sm"
                          variant={formData.customer_id === driver.id ? 'success' : 'outline-primary'}
                          onClick={() => selectDriver(driver)}
                        >
                          {formData.customer_id === driver.id ? t('selected') : t('select')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default SpecialBookingCreate;